import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  TablePagination,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  MilitaryTech as LevelIcon,
  AddCircleOutline as AddCircleIcon,
  RemoveCircleOutline as RemoveCircleIcon,
} from '@mui/icons-material';

// components
import { Typography } from '../../../components/Wrappers/Wrappers';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';
import levelService from '../../../services/level';

const GlassCard = ({ children, sx, ...props }) => {
  const theme = useTheme();
  return (
    <Card
      {...props}
      sx={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const LevelAward = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [designations, setDesignations] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [reward1, setReward1] = useState('');
  const [reward2, setReward2] = useState('');

  const [formData, setFormData] = useState({
    levelCode: '',
    rankName: '',
    designationId: '',
    minTeamBusiness: '',
    percentage: '',
    minSelfBusiness: '',
    incentiveAmount: '',
    rewardDescription: '',
    benefitType: 'reward',
    isRoyaltyApplicable: false,
    status: 'active',
  });

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', levelId: null });

  useEffect(() => {
    fetchLevels();
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    fetchDesignations();
    fetchAllLevels();
  }, []);

  const handleRefresh = () => {
    fetchLevels();
    fetchAllLevels();
    fetchDesignations();
  };

  const fetchDesignations = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${baseURL}/api/globalMasterDetails?type=designation`);
      if (response.data.success) {
        setDesignations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    }
  };

  const fetchAllLevels = async () => {
    try {
      const params = {
        benefitType: 'reward',
        limit: 1000, // Fetch all to filter out existing ranks
      };
      const response = await levelService.getLevels(params);
      if (response.status && Array.isArray(response.data)) {
        setAllLevels(response.data);
      }
    } catch (error) {
      console.error('Error fetching all levels:', error);
    }
  };

  const fetchLevels = async () => {
    try {
      setLoading(true);
      const params = {
        benefitType: 'reward',
        page,
        limit: rowsPerPage,
        search,
        status: statusFilter === 'all' ? '' : statusFilter
      };
      const response = await levelService.getLevels(params);
      if (response.status && Array.isArray(response.data)) {
        setLevels(response.data);
        setTotalCount(response.extra?.total || response.data.length);
      } else {
        setLevels([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching levels:', error);
      setAlertData({ open: true, message: 'Failed to fetch levels', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({
        levelCode: level.designationLevel ? `L${level.designationLevel}` : level.levelCode || '',
        rankName: level.designationName || level.rankName || '',
        designationId: level.designationId || '',
        minTeamBusiness: level.toTeamBusiness || level.minTeamBusiness || '',
        percentage: level.percentage || '',
        minSelfBusiness: level.toSelfBusiness || level.minSelfBusiness || '',
        incentiveAmount: level.incentiveAmount || '',
        rewardDescription: level.rewardDescription || '',
        benefitType: level.benefitType || 'reward',
        isRoyaltyApplicable: level.isRoyaltyApplicable === true,
        status: level.status === 'active' || level.status === true ? 'active' : 'inactive',
      });
      const rewards = level.rewardDescription ? level.rewardDescription.split(' | ') : ['', ''];
      setReward1(rewards[0] || '');
      setReward2(rewards[1] || '');
    } else {
      setEditingLevel(null);
      setFormData({
        levelCode: '',
        rankName: '',
        designationId: '',
        minTeamBusiness: '',
        percentage: '',
        minSelfBusiness: '',
        incentiveAmount: '',
        rewardDescription: '',
        benefitType: 'reward',
        isRoyaltyApplicable: false,
        status: 'active'
      });
      setReward1('');
      setReward2('');
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!formData.rankName || !formData.minTeamBusiness || !formData.percentage) {
      setAlertData({ open: true, message: 'Please fill all required fields', type: 'error' });
      return;
    }

    try {
      const payload = {
        ...formData,
        rewardAmount: reward1,
        rewardItem: reward2,
        rewardDescription: [reward1, reward2].filter(d => d.trim()).join(' | '),
        benefitType: 'reward',
        status: formData.status === 'active' ? 'active' : 'inactive',
        minTeamBusiness: Number(formData.minTeamBusiness),
        minSelfBusiness: Number(formData.minSelfBusiness),
        percentage: Number(formData.percentage),
        incentiveAmount: Number(formData.incentiveAmount || 0),
      };

      let response;
      if (editingLevel) {
        response = await levelService.updateLevel(editingLevel._id, payload);
      } else {
        response = await levelService.createLevel(payload);
      }

      if (response.status) {
        setAlertData({
          open: true,
          message: editingLevel ? 'Level updated successfully' : 'New level created successfully',
          type: 'success'
        });
        setOpen(false);
        fetchLevels();
        fetchAllLevels();
      }
    } catch (error) {
      console.error('Error saving level:', error);
      setAlertData({
        open: true,
        message: error.message || 'Failed to save level',
        type: 'error'
      });
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Level Reward',
      message: 'Are you sure you want to delete this level? This action cannot be undone.',
      levelId: id
    });
  };

  const confirmDelete = async () => {
    try {
      const response = await levelService.deleteLevel(confirmDialog.levelId);
      if (response.status) {
        setAlertData({ open: true, message: 'Level reward deleted', type: 'success' });
        fetchLevels();
        fetchAllLevels();
      }
    } catch (error) {
      console.error('Error deleting level:', error);
      setAlertData({ open: true, message: 'Failed to delete level', type: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  return (
    <Box sx={{
      pt: { xs: 1, md: 2 },
      px: { xs: 2, md: 4 },
      pb: { xs: 2, md: 4 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>
            Reward Structure
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Manage performance-based ranks and rewards.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <RefreshButton onClick={handleRefresh} loading={loading} />
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: { xs: 2, sm: 3 },
              py: 1,
              fontWeight: 700,
              width: { xs: '100%', sm: 'auto' },
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}40`,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 6px 16px 0 ${theme.palette.primary.main}50`
              }
            }}
          >
            Add New Level
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Ranks', value: allLevels.length, icon: <LevelIcon />, color: theme.palette.primary.main },
          { label: 'Top Percentage', value: allLevels.length > 0 ? `${Math.max(...allLevels.map(l => l.percentage || 0))}%` : '0%', icon: <TrendingUpIcon />, color: theme.palette.success.main },
          { label: 'Max Target', value: allLevels.length > 0 ? `₹${Math.max(...allLevels.map(l => l.minTeamBusiness || 0)).toLocaleString()}` : '₹0', icon: <TrophyIcon />, color: theme.palette.warning.main },
          { label: 'Active Levels', value: allLevels.filter(l => l.status === 'active' || l.status === true).length, icon: <GroupsIcon />, color: theme.palette.info.main },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <GlassCard>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}99 100%)`,
                  color: '#fff',
                  width: 48, height: 48,
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px 0 ${stat.color}30`
                }}>
                  {stat.icon}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>{stat.label.toUpperCase()}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>{stat.value}</Typography>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 4, p: { xs: 1.5, sm: 2 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by rank name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <AddIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20, transform: 'rotate(45deg)' }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </TextField>
          </Grid>
        </Grid>
      </GlassCard>

      {/* Data Table */}
      <Paper sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table">
              <TableHead sx={{ bgcolor: 'rgba(244, 247, 254, 0.5)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>LEVEL</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>RANK</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>TEAM BUSINESS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>PERCENTAGE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>SELF BUSINESS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>REWARD</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>STATUS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {levels.map((level, index) => (
                  <TableRow key={level._id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }, transition: '0.2s' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.9rem' }}>
                        {level.designationLevel ? `L${level.designationLevel}` : level.levelCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>
                          {level.designationName || level.rankName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                        ₹{(level.toTeamBusiness || level.minTeamBusiness || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${level.percentage}%`} size="small" sx={{ fontWeight: 700, bgcolor: `${theme.palette.success.main}15`, color: theme.palette.success.main }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>
                        ₹{(level.toSelfBusiness || level.minSelfBusiness || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {level.rewardDescription ? (
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {level.rewardDescription.split(' | ').map((reward, i) => (
                            <Tooltip key={i} title={reward}>
                              <Chip
                                label={reward}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  maxWidth: 150,
                                  borderColor: theme.palette.primary.main,
                                  color: theme.palette.primary.main
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>None</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={level.status === 'active' || level.status === true ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: (level.status === 'active' || level.status === true) ? `${theme.palette.success.main}15` : `${theme.palette.error.main}15`,
                          color: (level.status === 'active' || level.status === true) ? theme.palette.success.main : theme.palette.error.main
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpen(level)} size="small" sx={{ color: theme.palette.info.main, mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(level._id)} size="small" sx={{ color: theme.palette.error.main }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: '1px solid rgba(0,0,0,0.05)',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontWeight: 700,
              color: 'text.secondary',
              fontSize: '0.75rem'
            }
          }}
        />
      </Paper>

      {/* Creation/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', pt: 3 }}>
          {editingLevel ? 'Edit Reward Rank' : 'Create Reward Rank'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Level Code"
                placeholder="e.g. L1"
                variant="outlined"
                value={formData.levelCode}
                onChange={(e) => setFormData({ ...formData, levelCode: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={designations
                  .filter(d => {
                    const levelCode = `L${d.details?.level || ''}`;
                    // When editing, allow current rank; when creating, hide if level or designation already exists
                    return editingLevel?.levelCode === levelCode ||
                      editingLevel?.designationId === d._id ||
                      !allLevels.some(lvl =>
                        (lvl.levelCode === levelCode && levelCode !== 'L') ||
                        lvl.designationId === d._id ||
                        (lvl.designationName || lvl.rankName) === d.details?.designationName
                      );
                  })
                  .map((d) => d.details?.designationName || '')}
                value={formData.rankName}
                onChange={(event, newValue) => {
                  const selectedRank = newValue;
                  const designation = designations.find(d => d.details.designationName === selectedRank);
                  if (designation) {
                    setFormData({
                      ...formData,
                      levelCode: `L${designation.details.level || ''}`,
                      rankName: selectedRank,
                      designationId: designation._id,
                      minTeamBusiness: designation.details.toAmount || '',
                      minSelfBusiness: designation.details.toSelfBusiness || designation.details.minSelfBusiness || '',
                      percentage: designation.details.commission || '',
                    });
                  } else {
                    setFormData({ ...formData, rankName: selectedRank || '', designationId: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Rank (Designation)"
                    placeholder="Select or type rank..."
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Team Business Target"
                type="number"
                variant="outlined"
                value={formData.minTeamBusiness}
                onChange={(e) => setFormData({ ...formData, minTeamBusiness: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Percentage (%)"
                type="number"
                variant="outlined"
                value={formData.percentage}
                onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Self Business Target"
                type="number"
                variant="outlined"
                value={formData.minSelfBusiness}
                onChange={(e) => setFormData({ ...formData, minSelfBusiness: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
                REWARD DESCRIPTIONS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Reward Amount"
                    placeholder="e.g. 10000"
                    variant="outlined"
                    value={reward1}
                    onChange={(e) => setReward1(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reward Item"
                    placeholder="e.g. Laptop"
                    variant="outlined"
                    value={reward2}
                    onChange={(e) => setReward2(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <FormControlLabel
                  control={<Switch checked={formData.isRoyaltyApplicable} onChange={(e) => setFormData({ ...formData, isRoyaltyApplicable: e.target.checked })} color="primary" />}
                  label={<Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Royalty Applicable</Typography>}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <FormControlLabel
                  control={<Switch checked={formData.status === 'active'} onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })} color="primary" />}
                  label={<Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Active Status</Typography>}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              bgcolor: theme.palette.primary.main,
              borderRadius: '8px',
              px: 4,
              fontWeight: 700,
              boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}40`,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            Save Level
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={alertData.open}
        message={alertData.message}
        type={alertData.type}
        onClose={() => setAlertData({ ...alertData, open: false })}
      />

      <ConfirmModal
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
    </Box>
  );
};

export default LevelAward;
